<template>
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
      >
      <strong class="font-bold">Error! </strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <HowToConfigs v-if="showHowToPage"
      :show-how-to-page="showHowToPage"
      :external-navigation="true"
      @show-how-to-close="handleShowHowToPage"
    />

    <div v-if="isStaff || canViewConfigs" class="p-6">
      <!-- Header Section -->
      <div class="flex items-center justify-between mb-6">
        <h1 id="total_count" class="text-3xl font-bold text-gray-900 dark:text-white">
          Configs - {{ filteredConfigs.length }}
          <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
        </h1>

        <div class="text-base flex space-x-3 justify-between">
          <button
            type="button"
            class="flex items-center gap-2 animate-shake hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent px-3 py-2 rounded-lg transition-colors"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
            <span class="text-sm font-medium dark:text-gray-50 text-gray-900">Tutorial</span>
          </button>
          <button v-if="isStaff || canAddConfigs"
            id="add"
            @click="
              $router.push({ name: 'ConfigWorkSpaceDetails', params: { id: 'new' } })
            "
            class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
          >
            Add Config
          </button>
        </div>
        
      </div>
  
      <!-- Search and Filter Section -->
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
      >
        <!-- Search Bar -->
        <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
          <SearchBar
            v-model="searchQuery"
            placeholderText="Search configs by name..."
          />
        </div>

        <div class="mb-4 md:hidden">
          <button
            @click="showFilters = !showFilters"
            class="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
          >
            {{ showFilters ? "Hide Filters" : "Show Filters" }}
          </button>
        </div>
        
        <!-- Filters Section -->
        <div
          :class="[
            'md:flex md:items-center ',
            showFilters ? 'block' : 'hidden md:block',
          ]"
        >
          <FiltersSection
            :filters="dynamicFilters"
            v-model:selectedFilters="selectedFilters"
            :showFilters="showFilters"
            @update:showFilters="showFilters = $event"
            @applyFilters="applyFilters"
          />
        </div>
  
        <!-- Bulk Action Section -->
        <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
          <GlassSelect
            id="bulkactions"
            v-model="bulkAction"
            :options="isStaff || canDeleteConfigs ? [{ value: 'delete', label: 'Delete selected items' }] : []"
            placeholder="Bulk Actions"
            :valueKey="'value'"
            :labelKey="'label'"
          />
          <button
            id="apply"
            @click="applyBulkAction"
            :disabled="!bulkAction || selectedConfigs.length === 0"
            :class="[
              'btn focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
              bulkAction && selectedConfigs.length
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            ]"
          >
            Apply
          </button>
        </div>
      </div>

      <!-- Config Type Tabs -->
      <div class="mb-6">
        <div class="backdrop-blur-sm py-3 px-3 glass-card-lite">
          <nav class="flex flex-wrap items-center gap-3">
            <button
              v-for="tab in availableTabs"
              :key="tab.key"
              @click="selectTab(tab.key)"
              :class="[
                'bg-transparent px-4 py-2 font-bold transition-all duration-200 flex items-center gap-2 rounded-md border border-white glass-card whitespace-nowrap',
                selectedTab === tab.key
                  ? 'text-fuchsia-500'
                  : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500',
              ]"
            >
              {{ tab.label }}
              <span
                class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold"
                :class="selectedTab === tab.key
                  ? 'bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/40 dark:text-fuchsia-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'"
              >
                {{ tab.key === 'all' ? baseFilteredConfigs.length : (tabCounts[tab.key] || 0) }}
              </span>
            </button>
          </nav>
        </div>
      </div>

      <GenericTable
        ref="table"
        :columns="columns"
        :items="paginatedConfigs"
        itemKey="id"
        :selectable="true"
        @update:selectedItems="updateSelectedConfigs"
        @rowClick="goToConfigDetail"
      >
        <!-- Custom cell templates -->
         <template #config_label="{ item }">
          <div class="flex items-center gap-2">
            <span>{{ item.config_label }}</span>
            <span
              v-if="item.space_type"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
              :class="item.space_type === 'workspace'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                : 'bg-emerald-100 text-emerald-700 dark:bg-yellow-900/40 dark:text-yellow-300'"
            >
              {{ item.space_type }}
            </span>
          </div>
        </template>
        <template #created_at="{ value }">
          {{ formatDateTime(value) }}
        </template>
         <template #last_modified="{ value }">
        {{ formatDateTime(value) }}
      </template>
      </GenericTable>
  
      <!-- Pagination -->
      <div
        class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
      >
        <div class="flex items-center space-x-2">
          <button
            @click="changePage(-1)"
            :disabled="currentPage === 1"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Previous
          </button>
          <button
            @click="changePage(1)"
            :disabled="currentPage === totalPages"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Next
          </button>
        </div>
        <div class="text-gray-700 dark:text-white">
          Page {{ currentPage }} of {{ totalPages }}
        </div>
        <div>
          <GlassSelect
            v-model="itemsPerPage"
            :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
            :valueKey="'value'"
            :labelKey="'label'"
            placeholder="Items per page"
            @change="updatePagination"
          />
        </div>
      </div>
  
      <DeleteModal
        v-if="deletingItem"
        :deleting-item="deletingItem"
        :componet-objects="componetObjects"
        :is-deleting="isDeleting"
        @delete="confirmDelete"
        @close="deletingItem=false"
      />
    </div>
  </template>
  
  <script>
  import { getConfigs, deleteConfig } from "@/services/configService";
  import { getOrgs } from "@/services/wsOrgService";
  import { getAgentGroups } from "@/services/agentGroupService";
  import { getAgents } from "@/services/agentService";
  import SearchBar from "@/components/SearchBar.vue";
  import GenericTable from "@/components/GenericTable.vue";
  import { useStore } from "@/store";
  import HowToConfigs from "./HowToConfigs.vue";
  import FiltersSection from "@/components/FiltersSection.vue";
  import DeleteModal from "@/components/DeleteModal.vue";
  import { useTutorial } from "@/composables/tutorial";
  import GlassSelect from "@/components/GlassSelect.vue";

  export default {
    name: "ConfigWorkSpaceView",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    components: {
      SearchBar,
      GenericTable,
      HowToConfigs,
      FiltersSection,
      DeleteModal,
      GlassSelect,
    },
    data() {
      return {
        configs: [],
        showFilters: false,
        showHowToPage: false,
        searchQuery: "",
        selectedTab: "all",
        currentPage: 1,
        itemsPerPage: 10,
        selectedConfigs : [],
        deletingItem: false,
        bulkAction: "",
        columns: [
            { key: "config_label", label: "Config", sortable: true, width: "20%" },
            { key: "config_scope", label: "Level",  sortable: true, width: "15%" },
            { key: "operating_system", label: "OS", sortable: true, width: "15%" },
            { key: "organization_name", label: "Organisation", sortable: true, width: "15%" },
            { key: "created_at", label: "Created Date", sortable: true, width: "15%" },
            { key: "last_modified", label: "Last Modified Date", sortable: true, width: "15%" },
        ],
        error: "",
        organizations: [],
        groups: [],
        config_types : [
          { key:'workspaceconfig', label:'Workspace Config'},
          { key:'launcherconfig', label:'Launcher Config'},
          { key:'collectorconfig', label:'Collector Config'},
          { key:'alloyconfig', label:'Alloy Config'}
        ],
        canAddConfigs: false,
        canViewConfigs: false,
        canDeleteConfigs: false,
        dynamicFilters: [],
        selectedFilters: {
          organizationFilter: "",
          osFilter: "",
          levelFilter: "",
          groupFilter: "",
        },
        steps: [
          {
            element: '#total_count',
            action: 'display',
            value: '',
            message: 'Total count of items in the table.'
          },
          {
            element: '#searchbar',
            action: 'display',
            value: '',
            message: 'Search and filter configs.'
          },
          {
            element: '#table',
            action: 'display',
            value: '',
            message: 'Configs overview table displaying config type and level. (e.g group, organisation and agent)'
          },
          {
            element: '#bulkactions',
            action: 'display',
            value: 1,
            message: 'Select one or more items to enable bulk actions.'
          },
          {
            element: '#apply',
            action: 'display',
            message: 'Apply bulk actions (e.g., delete, archive, modify).'
          },
          {
            element: '#add',
            action: 'display',
            message: 'Add a new config.'
          }
        ],
        isDeleting: false,
      };
    },
    computed: {
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      }, 
      isFiltered() {
        return (
          this.searchQuery !== "" ||
          this.selectedFilters.organizationFilter !== "" ||
          this.selectedFilters.osFilter !== "" ||
          this.selectedFilters.levelFilter !== "" ||
          this.selectedFilters.groupFilter !== "" ||
          this.selectedTab !== "all"
        );
      },
      baseFilteredConfigs() {
        return this.configs.filter((config) => {
          const matchesSearch = (config?.config_label || "")
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase());

          const matchesNotStaffFilter = !this.isStaff
            ? config.config_type !== "workspaceconfig" &&
              config.config_type !== "launcherconfig"
            : true;

          const matchesOrg =
            this.selectedFilters.organizationFilter === "" ||
            config.organization === this.selectedFilters.organizationFilter;

          const matchesOs =
            this.selectedFilters.osFilter === "" ||
            config.operating_system === this.selectedFilters.osFilter;

          const matchesLevel =
            this.selectedFilters.levelFilter === "" ||
            config.config_level === this.selectedFilters.levelFilter;

          const matchesGroup =
            this.selectedFilters.groupFilter === "" ||
            config.group === this.selectedFilters.groupFilter;

          return (
            matchesSearch &&
            matchesNotStaffFilter &&
            matchesOrg &&
            matchesOs &&
            matchesLevel &&
            matchesGroup
          );
        });
      },
      filteredConfigs() {
        return this.selectedTab === "all"
          ? this.baseFilteredConfigs
          : this.baseFilteredConfigs.filter(
              (config) => config.config_type === this.selectedTab
            );
      },
      availableTabs() {
        const allowed = this.config_types.filter(
          (t) =>
            this.isStaff ||
            (t.key !== "workspaceconfig" && t.key !== "launcherconfig")
        );
        return [{ key: "all", label: "All" }, ...allowed];
      },
      tabCounts() {
        const counts = {};
        for (const config of this.baseFilteredConfigs) {
          counts[config.config_type] = (counts[config.config_type] || 0) + 1;
        }
        return counts;
      },
      totalPages() {
        return this.itemsPerPage === -1
          ? 1
          : Math.ceil(this.filteredConfigs.length / this.itemsPerPage);
      },
      paginatedConfigs() {
        if (this.itemsPerPage === -1) {
          return this.filteredConfigs;
        }
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredConfigs.slice(start, end);
      },
      componetObjects() {
        return this.selectedConfigs.map(config => {
          return {
            values: [
              { name: "Config", value: config.config_label || null },
              { name: "Space", value: config.space_type || null },
              { name: "Organisation", value: config.organization_name || null },
            ]
          };
        });
      },
    },
    watch: {
      searchQuery() {
        this.applyFilters();
      },
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
    },
    async mounted() {
      this.initSelectedTab();
      await this.fetchOrganisations();
      this.checkPermissions();
      await this.fetchConfig();
      this.initializeFilters();
    },
    methods: {
      async startTutorial() {
        await this._start(this.steps);
      },
      handleShowHowToPage(value){
        this.showHowToPage = value;
      },
      checkPermissions(){
        this.canAddConfigs = useStore.hasPermission("Can add configs");
        this.canDeleteConfigs = useStore.hasPermission("Can delete configs");
        this.canViewConfigs = useStore.hasPermission("Can view configs");
      },
       async fetchOrganisations(){
        try {
          this.organizations = await getOrgs();
        } catch (error) {
          console.error(error);
        }
      },
      async fetchConfig() {
        try {
            this.configs = await getConfigs();
            const orgs = await getOrgs();
            const agents = await getAgents();
            const groups = await getAgentGroups();
            this.groups = groups;

            this.configs = this.configs.map(config => {
              let orgName = null;


              const orgMap = Object.fromEntries(orgs.map((org) => [org.id, org.name]))
              const agentMap = Object.fromEntries(agents.map((agent) => [agent.licensekey, agent.organization]));
              const groupMap = Object.fromEntries(groups.map((group) => [group.id, group.organization]));


              if (config?.organization) orgName = orgMap[config.organization];
              if (config?.agent) orgName = orgMap[agentMap[config.agent]];
              if (config?.group) orgName = orgMap[groupMap[config.group]];

              let config_scope = "unknown";
              let config_level = "unknown";
              if (config.organization && !config.group && !config.agent) {
                config_scope = `Organisation`;
                config_level = "organisation";
              } else if (config.organization && config.group && !config.agent) {
                config_scope = `Group ${groups.find(item => item.id == config.group)?.name}`;
                config_level = "group";
              } else if (config.organization && config.agent) {
                config_scope = `Agent ${agents.find(item => item.licensekey == config.agent)?.licensekey}`;
                config_level = "agent";
              }

              return {
                ...config,
                organization_name: orgName,
                config_label: this.config_types.find(s => s.key === config?.config_type)?.label ?? config.config_type,
                config_scope,
                config_level
              };
            });

          this.applyFilters();
        } catch (error) {
          console.error("Error fetching configs:", error);
          if (error.response) {
            switch (error.response.status) {
              case 404:
                this.error = error.response.status +" The resource was not found. Please check the API endpoint."
                break;
              case 401:
                this.error = error.response.status + " Unauthorized access. Please check your authentication token or log in again."
                break;
              case 403:
                this.error = error.response.status + " You don't have permission to access this resource."
                            + " Contact a staff member to assign you the permissions to access this page. ";
                break;
              default:
                this.error = error.response.status =   error.response.data.detail || "Unknown error"
            }
          } else if (error.request) {
            alert(
              "No response received from the server. Please check your internet connection."
            );
          } else {
            alert(`An error occurred: ${error.message}`);
          }
        }
      },
      // A collector's workspace config shares its full scope: the same fields the
      // backend's uniqueness check uses (org, group, agent, OS and space type).
      findWorkspacePair(config) {
        return this.configs.find((item) =>
          item.config_type === "workspaceconfig" &&
          item.organization === config.organization &&
          item.group === config.group &&
          item.agent === config.agent &&
          item.operating_system === config.operating_system &&
          item.space_type === config.space_type
        );
      },
      goToConfigDetail(config) {
        const workspaceConfig = this.findWorkspacePair(config);

        this.$router.push({
          name: "ConfigWorkSpaceDetails",
          params: { id: config.id },
          query: { workspace: workspaceConfig?.id }
        });
      },
      updateSelectedConfigs(selectedItems) {
        this.selectedConfigs = selectedItems;
      },
      async applyBulkAction() {
        if (this.bulkAction === "delete" ) {
          this.deletingItem = true;
        }
        this.bulkAction = "";
      },
      async confirmDelete() {
        this.isDeleting = true;
        await this.deleteSelectedConfigs();
        this.deletingItem = false; 
        this.bulkAction = "";
      },
      async deleteSelectedConfigs() {
        if (this.selectedConfigs.length === 0) {
          alert("No configs selected for deletion");
          return;
        }

        try {

          const deletePromises = this.selectedConfigs.flatMap((config) => {
            const promises = [deleteConfig(config.id)];
            
            if (config.config_type === "collectorconfig") {
              const workspaceConfig = this.findWorkspacePair(config);

              if (workspaceConfig && !this.selectedConfigs.some(c => c.id === workspaceConfig.id)) {
                promises.push(deleteConfig(workspaceConfig.id));
              }
            }
            
            return promises;
          });
                              
          await Promise.all(deletePromises);
  
          this.configs = this.configs.filter(
            (config) => !this.selectedConfigs.some((item) => item.id === config.id)
          );
          await this.fetchConfig()

          this.$refs.table.clearSelection();
          this.applyFilters();
          this.selectedConfigs = [];
        } catch (error) {
          console.error("Error during bulk delete:", error);
          alert(
            "An error occurred while deleting organizations. Please try again."
          );
        }  finally {
          this.isDeleting = false;
        }
      },
      formatDateTime(dateString) {
        const options = {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
        };
        return new Date(dateString).toLocaleString(undefined, options);
      },
      applyFilters() {
        // Filtering itself is reactive via computed properties
        // (baseFilteredConfigs / filteredConfigs); we only reset paging here.
        this.currentPage = 1;
      },
      initSelectedTab() {
        const tab = this.$route.query.tab;
        const validTab =
          tab === "all" || this.config_types.some((t) => t.key === tab);
        this.selectedTab = validTab ? tab : "all";
      },
      specificTabUrl(){
        this.$router.push({
          name: "ConfigWorkSpaceView",
          query: { tab: this.selectedTab }
        });
      },
      selectTab(key) {
        this.selectedTab = key;
        this.currentPage = 1;
        this.specificTabUrl()
      },
      changePage(delta) {
        this.currentPage += delta;
      },
      updatePagination() {
        this.currentPage = 1;
      },
      initializeFilters() {
        this.dynamicFilters = [
          {
            name: "organizationFilter",
            label: "Organisation",
            options: [{ value: "", label: "All" }].concat(
              Object.keys(this.organizations).map((orgId) => ({
                value: this.organizations[orgId]?.id,
                label: this.organizations[orgId]?.name,
              }))
            ),
          },
          {
            name: "osFilter",
            label: "OS",
            options: [
              { value: "", label: "All" },
              { value: "Windows", label: "Windows" },
              { value: "Linux", label: "Linux" },
            ],
          },
          {
            name: "levelFilter",
            label: "Level",
            options: [
              { value: "", label: "All" },
              { value: "organisation", label: "Organisation" },
              { value: "group", label: "Group" },
              { value: "agent", label: "Agent" },
            ],
          },
          {
            name: "groupFilter",
            label: "Agent Group",
            options: [{ value: "", label: "All" }].concat(
              this.groups.map((group) => ({
                value: group.id,
                label: group.name,
              }))
            ),
          },
        ];
      },
    },
  };
  </script>
  